import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Youtube, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Logo SVG Component (for dark background)
const LogoLight = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 280 55" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 27 L28 8 L46 27 L28 19 L10 27Z" fill="#D4A03A" />
    <path d="M28 19 L46 27 L28 46 L28 36 L16 27 L28 19Z" fill="#FFFFFF" fillOpacity="0.9" />
    <text x="56" y="35" fontFamily="Space Grotesk, sans-serif" fontSize="26" fontWeight="700" fill="#FFFFFF">GRC</text>
    <text x="110" y="35" fontFamily="Space Grotesk, sans-serif" fontSize="26" fontWeight="700" fill="#D4A03A">EDUCATORS</text>
    <text x="56" y="50" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="9" fontWeight="500" fill="rgba(255,255,255,0.7)" letterSpacing="0.1em">YOUR PATH TO EXCELLENCE</text>
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [companyInfo, setCompanyInfo] = useState({
    company_name: 'GRC EDUCATORS INC',
    address: '1312 17th Street, Unit #2229',
    city: 'Denver',
    state: 'Colorado',
    zip_code: '80202',
    country: 'USA',
    phone: '713-401-9995',
    email: 'support@grceducators.com',
  });

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/public/company-info`);
      if (response.ok) {
        const data = await response.json();
        setCompanyInfo(data);
      }
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    }
  };

  // Build full address
  const fullAddress = [
    companyInfo.city,
    companyInfo.state,
    companyInfo.zip_code,
    companyInfo.country
  ].filter(Boolean).join(', ');

  const footerLinks = {
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Contact', href: '/contact' },
    ],
    resources: [
      { name: 'Industries', href: '/industries' },
      { name: 'All Webinars', href: '/webinars' },
      { name: 'Join Speaker Panel', href: '/join-speaker' },
      { name: 'Articles', href: '/articles' },
    ],
    support: [
      { name: 'Help Center', href: '/contact' },
      { name: 'Terms of Use', href: '/terms-of-use' },
      { name: 'Privacy Policy', href: '/privacy-policy' },
      { name: 'Refund Policy', href: '/refund-policy' },
    ],
  };

  const socialLinks = [
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Youtube, href: '#', label: 'YouTube' },
  ];

  return (
    <footer className="bg-[#09090b] text-white relative overflow-hidden border-t border-zinc-800" data-testid="footer">
      <div className="container-custom relative z-10">
        {/* Newsletter Section */}
        <div className="py-16 border-b border-white/10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-gold-400" />
              <span className="text-gold-300 text-sm font-medium">Stay Updated</span>
            </div>
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Subscribe to Our Newsletter
            </h3>
            <p className="text-slate-400 mb-8 max-w-lg mx-auto">
              Get the latest updates on new webinars, compliance news, and exclusive offers.
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="h-14 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-xl focus:border-gold-400 flex-1"
              />
              <Button className="h-14 bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold px-8 rounded-md">
                Subscribe
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <LogoLight className="h-14 w-auto" />
            </Link>
            <p className="text-slate-400 leading-relaxed mb-8 max-w-sm">
              Your Path to Excellence. Delivering quality governance, compliance, and regulatory webinars to industry professionals across North America and beyond.
            </p>
            <div className="space-y-4">
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-4 text-slate-400 hover:text-gold-400 transition-colors group"
              >
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-gold-500/20 transition-colors">
                  <Mail className="w-5 h-5 text-gold-400" />
                </div>
                <span>{companyInfo.email}</span>
              </a>
              <a
                href={`tel:+1${companyInfo.phone?.replace(/-/g, '')}`}
                className="flex items-center gap-4 text-slate-400 hover:text-gold-400 transition-colors group"
              >
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-gold-500/20 transition-colors">
                  <Phone className="w-5 h-5 text-gold-400" />
                </div>
                <span>{companyInfo.phone}</span>
              </a>
              <div className="flex items-center gap-4 text-slate-400">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-gold-400" />
                </div>
                <span>{companyInfo.address}<br/>{fullAddress}</span>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading text-lg font-bold mb-6 text-white">Company</h4>
            <ul className="space-y-4">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-gold-400 transition-colors inline-flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold-500/50 group-hover:bg-gold-400 transition-colors" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-heading text-lg font-bold mb-6 text-white">Resources</h4>
            <ul className="space-y-4">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-gold-400 transition-colors inline-flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold-500/50 group-hover:bg-gold-400 transition-colors" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="font-heading text-lg font-bold mb-6 text-white">Support</h4>
            <ul className="space-y-4">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-gold-400 transition-colors inline-flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold-500/50 group-hover:bg-gold-400 transition-colors" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-slate-500 text-sm">
              © {currentYear} {companyInfo.company_name}. All rights reserved.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center text-slate-400 hover:bg-gold-500 hover:text-navy-900 transition-all duration-300"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
